import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { r_chevron_down } from '../../../icons';
import isTouchDevice from '../../../lib/isTouchDevice';
import getRenderable from '../../../lib/getRenderable';
import Icon from '../Icon';
import Popover from '../Popover';
import { Option, OptionWrapper } from './Option';
import { BASE_CLASSES as OPTION_BASE_CLASSES, ICON_CLASS } from './Option/constants';
import {
    BASE_CLASSES,
    OPTION_CHILD_ERROR,
    LOAD_EVENT,
    COMPLETE
} from './constants';
import './index.scss';

/**
 * Validates a provided Select child is usable.
 * @param {React.Component} child The child to validate.
 */
const validateChild = (child) => {
    if (Array.isArray(child)) {
        child.forEach(validateChild);
    } else if (!child.type || child.type !== Option) {
        throw new Error(OPTION_CHILD_ERROR);
    }
};

/**
 * Calculates the Select component's minimum width by measuring every option provided and selecting the biggest width out of the provided options.
 * @param  {React.Component[]} options The options provided.
 * @param  {HTMLElement} [container] The container element to measure from. Needed mainly due to FIT's style scoping.
 * @return {Number} The minimum width.
 */
const getMinimumWidth = (options, container = document.body) => {
    let minWidth = 0;
    const el = document.createElement('div');
    const dummyIcon = document.createElement('span');
    const dummyText = document.createElement('span');

    Object.assign(el.style, {
        display: 'inline-block',
        width: 'auto',
        visibility: 'hidden'
    });
    dummyIcon.classList.add(ICON_CLASS);
    el.classList.add(OPTION_BASE_CLASSES);

    el.appendChild(dummyIcon);
    el.appendChild(dummyText);
    container.appendChild(el);

    options.forEach(({ label }) => {
        dummyText.innerText = label;

        if (el.offsetWidth > minWidth) {
            minWidth = el.offsetWidth;
        }
    });

    container.removeChild(el);

    return minWidth;
};

class Select extends React.PureComponent {
    constructor(props) {
        super(props);

        this.popover = null;
        this.observer = null;
        this.controlled = typeof props.value !== 'undefined';
        this.options = [];
        this.populateOptions();

        this.state = {
            selected: this.initialValue,
            minWidth: 0
        };

        this.onChange = this.onChange.bind(this);
        this.setMinimumWidth = this.setMinimumWidth.bind(this);
        this.handleObserver = this.handleObserver.bind(this);
    }

    componentDidMount() {
        // This is deferred so we can assure the measure executes after the CSS has been rendered.

        const isLoaded = document.readyState === COMPLETE;

        if ('MutationObserver' in window) {
            this.observer = new window.MutationObserver(this.handleObserver);
        }

        if (isLoaded) {
            this.setMinimumWidth();
        } else {
            window.addEventListener(LOAD_EVENT, this.setMinimumWidth);
        }
    }

    componentWillUnmount() {
        this.observer && this.observer.disconnect();
        window.removeEventListener(LOAD_EVENT, this.setMinimumWidth);
    }

    get initialValue() {
        const {
            defaultValue,
            value
        } = this.props;

        if (!this.controlled && typeof defaultValue === 'undefined') {
            return this.options[0];
        }

        const targetValue = this.controlled ? value : defaultValue;

        return this.getOptionByValue(targetValue);
    }

    get wrapperClasses() {
        const {
            disabled
        } = this.props;

        return classnames('fit-select-wrapper', {
            'fit-select-wrapper-disabled': disabled
        });
    }

    get classes() {
        const {
            disabled,
            className
        } = this.props;

        return classnames(BASE_CLASSES, className, {
            'fit-select-disabled': disabled
        });
    }

    get selected() {
        if (!this.state) { return {}; } // The component wasn't initialized yet.

        const { value } = this.props;
        const { selected } = this.state;

        if (this.controlled) {
            return this.getOptionByValue(value);
        }

        return selected;
    }

    get children() {
        const { children } = this.props;

        return Array.isArray(children)
            ? _.flatten(children)
            : [children]; // When passed a single child
    }

    handleObserver() {
        this.observerRegistered = true;

        const { minWidth } = this.state;

        if (minWidth) {
            this.observer.disconnect();
            return;
        }

        const isVisible = this.dummyWrapper && this.dummyWrapper.offsetParent;

        if (isVisible) {
            this.setMinimumWidth();
        }
    }

    setMinimumWidth() {
        const { minWidth } = this.state;

        if (minWidth) { return; }

        const width = getMinimumWidth(this.options, this.dummyWrapper);

        if (!width && this.observer && !this.observerRegistered) {
            this.observer.observe(document.documentElement || document.body, {
                attributes: true,
                subtree: true
            });
            return;
        }

        this.setState({
            minWidth: width
        });
    }

    getOptionByValue(targetValue) {
        return this.options.find(({ value }) => value === targetValue) || {};
    }

    getOptionByLabel(targetLabel) {
        return this.options.find(({ label }) => label === targetLabel) || {};
    }

    /**
     * Populates this Select's "options" key with the up-to-date options passed, validating each one on the go.
     */
    populateOptions() {
        this.options = this.children.map((option) => {
            validateChild(option);
            const { props } = option;

            return {
                value: props.value,
                label: props.children
            };
        });
    }

    generateMenu() {
        this.populateOptions();

        const renderableOptions = this.children.map((option, index) => {
            const { props } = option;
            const enrichedProps = {
                index,
                selected: this.selected.value === props.value,
                key: [index, props.value].join()
            };

            if (!props.disabled) {
                enrichedProps.onChange = this.onChange;
            }

            return getRenderable(OptionWrapper, props, enrichedProps);
        });

        return renderableOptions;
    }

    onChange(e, index) {
        const {
            disabled,
            onChange
        } = this.props;

        if (disabled) { return; }

        const selected = this.options[index];

        if (!this.controlled) {
            this.setState({
                selected
            });
        }

        onChange(e, index, selected.value);
        this.popover.close();
    }

    render() {
        const {
            children,
            disabled,
            defaultValue,
            value,
            name,
            onChange,
            placeholder
        } = this.props;
        const { minWidth } = this.state;
        const showPlaceHolder = placeholder && this.selected.value === undefined;
        const nativeSelectProps = {
            children,
            disabled,
            defaultValue,
            value,
            name,
            onChange: (e) => {
                const { selectedIndex, value } = e.target;
                const index = placeholder ? selectedIndex - 1 : selectedIndex;

                if (!this.controlled) {
                    this.setState({
                        selected: this.options[index]
                    });
                }

                onChange(e, index, value);
            }
        };

        if (isTouchDevice) {
            return (
                <select {...nativeSelectProps} className={classnames(this.classes, { 'with-placeholder': showPlaceHolder })}>
                    {placeholder && <Option hidden disabled value="">{placeholder}</Option>}
                    {children}
                </select>
            );
        }

        // The element hasn't rendered properly yet and doesn't have a minimum width set
        if (!minWidth) {
            return <div ref={(el) => this.dummyWrapper = el}/>;
        }

        return (
            <Popover className={this.wrapperClasses}
                contentClassName={this.classes}
                content={this.generateMenu()}
                position={Popover.POSITIONS.BOTTOM_LEFT}
                style={{ minWidth: minWidth + 2 }} // Compensation for the Popover's border.
                ref={(el) => this.popover = el}>
                <span className={classnames('fit-select-value', { 'with-placeholder': showPlaceHolder })}
                    style={{ minWidth: `${minWidth}px` }}>
                    { this.selected.label || placeholder }
                </span>
                <Icon size={11}
                    className="fit-select-caret">
                    {r_chevron_down}
                </Icon>
                <input type="hidden" name={name} value={this.selected.value}/>
            </Popover>
        );
    }
}

Select.propTypes = {
    /**
     * The `<Select>`'s options.
     * `<Option>`'s `children` may **only be a number or a string**, due to [React's limitations with rendering elements inside `<option>`](https://github.com/facebook/react/issues/13586)
     */
    children: PropTypes.arrayOf(Option).isRequired,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string,
    /**
     * Whether or not the select is disabled.
     * A disabled select is not clickable, or hoverable.
     */
    disabled: PropTypes.bool,
    /**
     * The component's initial value.
     */
    defaultValue: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    /**
     * The component's selected value.
     */
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    /**
     * Similar to the native `<select>` `name`.
     */
    name: PropTypes.string,
    /**
     * A callback function to be executed when the component's value changes.
     */
    onChange: PropTypes.func,
    /**
     * A text that will be displayed before the user has selected an option.
     */
    placeholder: PropTypes.string
};

Select.defaultProps = {
    disabled: false,
    placeholder: '',
    onChange: () => null
};

Object.assign(Select, { Option });

export { Option };
export default Select;
